import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  notificationService,
  Notification,
  CreateNotificationDto,
  SendEmailDto,
} from '../../services/notificationService';
import { getApiUrl, fetchWithTimeout } from '../../config/api';
import { useToast } from '../../context/ToastContext';
import { useConfirmDialog } from '../../context/ConfirmContext';
import styles from '../../styles/modules/admin/AdminNotifications.module.css';
import adminCommonStyles from '../../styles/modules/admin/AdminCommon.module.css';
import headerStyles from '../../styles/modules/Header.module.css';

interface User {
  _id: string;
  fullName: string;
  email: string;
  role: string;
}

const AdminNotifications: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { confirm, confirmDelete } = useConfirmDialog();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingNotification, setEditingNotification] = useState<Notification | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedNotification, setSelectedNotification] = useState<string | null>(null);
  const [stats, setStats] = useState<any>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [filterActive, setFilterActive] = useState<boolean | undefined>(true);
  const [filterPriority, setFilterPriority] = useState('');
  const [filterTargetType, setFilterTargetType] = useState('');
  
  const [notificationType, setNotificationType] = useState<'in-app' | 'email'>('in-app');

  const [formData, setFormData] = useState<CreateNotificationDto>({
    title: '',
    content: '',
    targetType: 'all',
    priority: 'normal',
    displayType: 'dismissible',
    category: 'announcement',
  });
  
  const [emailFormData, setEmailFormData] = useState<SendEmailDto>({
    targetType: 'all',
    subject: '',
    html: '',
  });
  const [userSearchFilter, setUserSearchFilter] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await notificationService.getAllNotifications({
        page: currentPage,
        limit: 10,
        isActive: filterActive,
        priority: filterPriority || undefined,
        targetType: filterTargetType || undefined,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });
      setNotifications(response.notifications);
      setTotalPages(response.pagination.totalPages);
    } catch (err: any) {
      console.error('Failed to load notifications:', err.message || err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterActive, filterPriority, filterTargetType]);

  useEffect(() => {
    fetchNotifications();
    fetchUsers();
  }, [fetchNotifications]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetchWithTimeout(getApiUrl('/users'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchStats = async (notificationId: string) => {
    try {
      const statsData = await notificationService.getNotificationStats(notificationId);
      setStats(statsData);
    } catch (err: any) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (notificationType === 'email') {
      setEmailFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleEmailTargetTypeChange = (value: string) => {
    setEmailFormData((prev) => ({
      ...prev,
      targetType: value as 'specific' | 'list' | 'role' | 'all',
      targetUsers: undefined,
      targetRole: undefined,
    }));
  };

  const handleAddUser = (userId: string) => {
    if (notificationType === 'email') {
      const selectedUsers = emailFormData.targetUsers || [];
      if (!selectedUsers.includes(userId)) {
        const newUsers = emailFormData.targetType === 'specific' ? [userId] : [...selectedUsers, userId];
        setEmailFormData((prev) => ({
          ...prev,
          targetUsers: newUsers,
        }));
      }
    } else {
      const selectedUsers = formData.targetUsers || [];
      if (!selectedUsers.includes(userId)) {
        const newUsers = formData.targetType === 'specific' ? [userId] : [...selectedUsers, userId];
        setFormData((prev) => ({
          ...prev,
          targetUsers: newUsers,
        }));
      }
    }
  };

  const handleRemoveUser = (userId: string) => {
    if (notificationType === 'email') {
      setEmailFormData((prev) => ({
        ...prev,
        targetUsers: (prev.targetUsers || []).filter((id) => id !== userId),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        targetUsers: (prev.targetUsers || []).filter((id) => id !== userId),
      }));
    }
  };

  const filteredUsers = users.filter((user) => {
    const searchTerm = userSearchFilter.toLowerCase();
    return (
      user.fullName.toLowerCase().includes(searchTerm) ||
      user.email.toLowerCase().includes(searchTerm)
    );
  });

  const selectedUsers = (notificationType === 'email' ? (emailFormData.targetUsers || []) : (formData.targetUsers || [])).map((userId) =>
    users.find((u) => u._id === userId)
  ).filter(Boolean) as User[];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (notificationType === 'email') {
        // Email notification submission
        const emailData = { ...emailFormData };
        
        if (emailData.targetType === 'all') {
          delete emailData.targetUsers;
          delete emailData.targetRole;
        } else if (emailData.targetType === 'role') {
          delete emailData.targetUsers;
        } else if (emailData.targetType === 'specific' || emailData.targetType === 'list') {
          delete emailData.targetRole;
        }

        // Validate that at least one content type is provided
        if (!emailData.text && !emailData.html) {
          showToast({ message: 'Please provide either plain text or HTML content', type: 'error' });
          setLoading(false);
          return;
        }

        await notificationService.sendEmailNotification(emailData);
        showToast({ message: 'Email notification sent successfully', type: 'success' });
      } else {
        // In-app notification submission
        const dataToSend = { ...formData };

        if (dataToSend.targetType === 'all') {
          delete dataToSend.targetUsers;
          delete dataToSend.targetRole;
        } else if (dataToSend.targetType === 'role') {
          delete dataToSend.targetUsers;
        } else if (dataToSend.targetType === 'specific' || dataToSend.targetType === 'list') {
          delete dataToSend.targetRole;
        }

        if (editingNotification) {
          await notificationService.updateNotification(editingNotification._id, dataToSend);
          showToast({ message: 'Notification updated successfully', type: 'success' });
        } else {
          await notificationService.createNotification(dataToSend);
          showToast({ message: 'Notification created successfully', type: 'success' });
        }
      }

      resetForm();
      fetchNotifications();
    } catch (err: any) {
      showToast({ message: err.message || 'Failed to save notification', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      targetType: 'all',
      priority: 'normal',
      displayType: 'dismissible',
      category: 'announcement',
    });
    setEmailFormData({
      targetType: 'all',
      subject: '',
      html: '',
    });
    setNotificationType('in-app');
    setShowCreateForm(false);
    setEditingNotification(null);
  };

  const handleEdit = (notification: Notification) => {
    setEditingNotification(notification);
    setFormData({
      title: notification.title,
      content: notification.content,
      targetType: notification.targetType,
      targetUsers: notification.targetUsers,
      targetRole: notification.targetRole,
      priority: notification.priority,
      displayType: notification.displayType,
      category: notification.category,
      expiresAt: notification.expiresAt
        ? new Date(notification.expiresAt).toISOString().slice(0, 16)
        : undefined,
    });
    setShowCreateForm(true);
  };

  const handleDeactivate = async (id: string) => {
    const confirmed = await confirm({
      title: 'Deactivate notification?',
      message: 'Are you sure you want to deactivate this notification?',
      confirmLabel: 'Deactivate',
      cancelLabel: 'Cancel',
      tone: 'default'
    });

    if (!confirmed) {
      return;
    }

    try {
      await notificationService.deactivateNotification(id);
      showToast({ message: 'Notification deactivated successfully', type: 'success' });
      fetchNotifications();
    } catch (err: any) {
      showToast({ message: err.message || 'Failed to deactivate notification', type: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirmDelete({
      title: t('admin.notifications.confirmDelete'),
      message: t('admin.notifications.confirmDelete'),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel')
    });
    if (!confirmed) {
      return;
    }

    try {
      await notificationService.deleteNotification(id);
      showToast({ message: t('admin.notifications.deleteSuccess'), type: 'success' });
      fetchNotifications();
    } catch (err: any) {
      showToast({ message: err.message || t('admin.notifications.deleteFailed'), type: 'error' });
    }
  };

  const handleViewStats = async (notification: Notification) => {
    setSelectedNotification(notification._id);
    await fetchStats(notification._id);
  };

  const getPriorityClass = (priority: string) => {
    const priorityMap: Record<string, string> = {
      urgent: styles.priorityUrgent,
      high: styles.priorityHigh,
      normal: styles.priorityNormal,
      low: styles.priorityLow,
    };

    return priorityMap[priority] || styles.priorityNormal;
  };

  return (
    <div className={styles.adminNotifications}>
      <div className={styles.listPageHeader}>
        <button
          className={headerStyles.backButton}
          onClick={() => navigate('/admin')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>
        <h1 className={styles.headerTitle}>Notification Management</h1>
        <button
          className={styles.actionButton}
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Cancel' : '+ Create Notification'}
        </button>
      </div>

      <div className={styles.pageContent}>
        <div className={styles.userSectionCard}>
          <div className={styles.sectionHeader}>
            <h2>Manage Notifications</h2>
            <span className={styles.userCount}>
              {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className={styles.controlsSection}>
            <div className={styles.filters}>
              <select
                value={filterActive === undefined ? '' : filterActive.toString()}
                onChange={(e) =>
                  setFilterActive(e.target.value === '' ? undefined : e.target.value === 'true')
                }
                className={styles.filterSelect}
                aria-label="Filter by status"
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            <div className={styles.filters}>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className={styles.filterSelect}
                aria-label="Filter by priority"
              >
                <option value="">All Priorities</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="normal">Normal</option>
                <option value="low">Low</option>
              </select>
            </div>

            <div className={styles.filters}>
              <select
                value={filterTargetType}
                onChange={(e) => setFilterTargetType(e.target.value)}
                className={styles.filterSelect}
                aria-label="Filter by target type"
              >
                <option value="">All Targets</option>
                <option value="all">All Users</option>
                <option value="role">Role</option>
                <option value="list">List</option>
                <option value="specific">Specific</option>
              </select>
            </div>
          </div>

          {loading && !showCreateForm ? (
            <div className={styles.sectionLoading}>Loading notifications...</div>
          ) : (
            <div className={styles.tableContainer}>
              <table className={styles.usersTable}>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Target</th>
                    <th>Priority</th>
                    <th>Display Type</th>
                    <th>Read %</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {notifications.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="no-data">
                        No notifications found
                      </td>
                    </tr>
                  ) : (
                    notifications.map((notification) => (
                      <tr key={notification._id}>
                        <td>
                          <span className={getPriorityClass(notification.priority)}>
                            {notification.title}
                          </span>
                        </td>
                        <td>
                          {notification.targetType === 'role'
                            ? `Role: ${notification.targetRole}`
                            : notification.targetType.charAt(0).toUpperCase() +
                              notification.targetType.slice(1)}
                        </td>
                        <td>
                          <span className={`${styles.badge} ${getPriorityClass(notification.priority)}`}>
                            {notification.priority}
                          </span>
                        </td>
                        <td>{notification.displayType}</td>
                        <td>
                          {notification.stats
                            ? `${notification.stats.readPercentage.toFixed(1)}%`
                            : '-'}
                        </td>
                        <td>
                          <span
                            className={`${styles.statusBadge} ${
                              notification.isActive ? styles.statusActive : styles.statusInactive
                            }`}
                          >
                            {notification.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>{new Date(notification.createdAt).toLocaleDateString(t('common.locale') || 'en-US')}</td>
                        <td className={styles.actionsCell}>
                          <button
                            className={adminCommonStyles.btnView}
                            onClick={() => handleViewStats(notification)}
                            title="View Stats"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="12" y1="20" x2="12" y2="10"/>
                              <line x1="18" y1="20" x2="18" y2="4"/>
                              <line x1="6" y1="20" x2="6" y2="16"/>
                            </svg>
                          </button>
                          <button
                            className={adminCommonStyles.btnView}
                            onClick={() => handleEdit(notification)}
                            title="Edit"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                          {notification.isActive && (
                            <button
                              className={`${adminCommonStyles.btnToggle} ${adminCommonStyles.btnSuspend}`}
                              onClick={() => handleDeactivate(notification._id)}
                              title="Deactivate"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                              </svg>
                            </button>
                          )}
                          <button
                            className={adminCommonStyles.btnDelete}
                            onClick={() => handleDelete(notification._id)}
                            title="Delete"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={styles.paginationBtn}
              >
                Previous
              </button>
              <span className={styles.paginationInfo}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className={styles.paginationBtn}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {selectedNotification && stats && (
        <div className={styles.statsModal} onClick={() => setSelectedNotification(null)}>
          <div className={styles.statsContent} onClick={(e) => e.stopPropagation()}>
            <h3>Notification Statistics</h3>
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <h4>Total Recipients</h4>
                <p className={styles.statValue}>{stats.statistics.totalRecipients}</p>
              </div>
              <div className={styles.statCard}>
                <h4>Read Count</h4>
                <p className={styles.statValue}>{stats.statistics.readCount}</p>
              </div>
              <div className={styles.statCard}>
                <h4>Read Rate</h4>
                <p className={styles.statValue}>{stats.statistics.readPercentage.toFixed(1)}%</p>
              </div>
              <div className={styles.statCard}>
                <h4>Dismissed</h4>
                <p className={styles.statValue}>{stats.statistics.dismissedCount}</p>
              </div>
            </div>

            {stats.recentReads && stats.recentReads.length > 0 && (
              <div className={styles.recentReads}>
                <h4>Recent Reads</h4>
                <table>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Read At</th>
                      <th>Views</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recentReads.map((read: any, index: number) => (
                      <tr key={index}>
                        <td>{read.user?.fullName || 'Unknown'}</td>
                        <td>{new Date(read.readAt).toLocaleString()}</td>
                        <td>{read.viewCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <button className="btn btn-secondary" onClick={() => setSelectedNotification(null)}>
              Close
            </button>
          </div>
        </div>
      )}

      {showCreateForm && (
        <div className={adminCommonStyles.modalOverlay} onClick={() => setShowCreateForm(false)}>
          <div className={adminCommonStyles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={adminCommonStyles.modalHeader}>
              <h2>{editingNotification ? 'Edit Notification' : 'Create New Notification'}</h2>
              <button className={adminCommonStyles.modalClose} onClick={() => setShowCreateForm(false)}>
                ✕
              </button>
            </div>
            <div className={adminCommonStyles.modalBody}>
              {!editingNotification && (
                <div className={styles.notificationTypeTabs}>
                  <button
                    type="button"
                    className={`${styles.notificationTypeTab} ${notificationType === 'in-app' ? styles.active : ''}`}
                    onClick={() => setNotificationType('in-app')}
                  >
                    📱 In-App Notification
                  </button>
                  <button
                    type="button"
                    className={`${styles.notificationTypeTab} ${notificationType === 'email' ? styles.active : ''}`}
                    onClick={() => setNotificationType('email')}
                  >
                    ✉️ Email Notification
                  </button>
                </div>
              )}
              <form 
                id="notificationForm" 
                onSubmit={handleSubmit}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1.5rem'
                }}
              >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className={styles.formGroup} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <label htmlFor="targetType">Target Type *</label>
                    <select
                      id="targetType"
                      name="targetType"
                      value={notificationType === 'email' ? emailFormData.targetType : formData.targetType}
                      onChange={(e) => {
                        if (notificationType === 'email') {
                          handleEmailTargetTypeChange(e.target.value);
                        } else {
                          handleInputChange(e);
                        }
                      }}
                      disabled={!!editingNotification}
                      style={{ width: '100%' }}
                    >
                      <option value="all">All Users</option>
                      <option value="role">Specific Role</option>
                      <option value="list">List of Users</option>
                      <option value="specific">Specific User</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {(notificationType === 'email' ? emailFormData.targetType : formData.targetType) === 'role' && (
                    <div className={styles.formGroup} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <label htmlFor="targetRole">Target Role *</label>
                      <select
                        id="targetRole"
                        name="targetRole"
                        value={notificationType === 'email' ? (emailFormData.targetRole || '') : (formData.targetRole || '')}
                        onChange={(e) => {
                          if (notificationType === 'email') {
                            setEmailFormData((prev) => ({
                              ...prev,
                              targetRole: e.target.value as 'staff' | 'employer',
                            }));
                          } else {
                            handleInputChange(e);
                          }
                        }}
                        required
                        disabled={!!editingNotification}
                        style={{ width: '100%' }}
                      >
                        <option value="">Select Role</option>
                        <option value="staff">Staff</option>
                        <option value="employer">Employer</option>
                      </select>
                    </div>
                  )}

                  {((notificationType === 'email' ? emailFormData.targetType : formData.targetType) === 'list' || (notificationType === 'email' ? emailFormData.targetType : formData.targetType) === 'specific') && (
                    <div className={styles.formGroup} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <label htmlFor="targetUsers">
                        Target Users * {(notificationType === 'email' ? emailFormData.targetType : formData.targetType) === 'list' ? '(Multiple)' : '(Single)'}
                      </label>
                      <div className="user-selection-container">
                        {selectedUsers.length > 0 && (
                          <div className="selected-users-tags">
                            {selectedUsers.map((user) => (
                              <div key={user._id} className="user-tag">
                                <span>{user.fullName}</span>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveUser(user._id)}
                                  className="tag-remove-btn"
                                  title="Remove user"
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="user-dropdown-wrapper">
                          <input
                            type="text"
                            placeholder="Search users by name or email..."
                            value={userSearchFilter}
                            onChange={(e) => setUserSearchFilter(e.target.value)}
                            onFocus={() => setShowUserDropdown(true)}
                            className="user-search-input"
                            disabled={!!editingNotification || ((notificationType === 'email' ? emailFormData.targetType : formData.targetType) === 'specific' && selectedUsers.length > 0)}
                          />
                          {showUserDropdown && (
                            <div className="user-dropdown-list">
                              {filteredUsers.length === 0 ? (
                                <div className="dropdown-empty">No users found</div>
                              ) : (
                                filteredUsers.map((user) => (
                                  <button
                                    key={user._id}
                                    type="button"
                                    onClick={() => {
                                      handleAddUser(user._id);
                                      setUserSearchFilter('');
                                      if ((notificationType === 'email' ? emailFormData.targetType : formData.targetType) === 'specific') {
                                        setShowUserDropdown(false);
                                      }
                                    }}
                                    className={`${styles.dropdownUserItem} ${
                                      selectedUsers.some((u) => u._id === user._id) ? 'selected' : ''
                                    }`}
                                    disabled={!!editingNotification}
                                  >
                                    <div className="user-info">
                                      <div className={styles.userName}>{user.fullName}</div>
                                      <div className={styles.userEmail}>{user.email}</div>
                                    </div>
                                    {selectedUsers.some((u) => u._id === user._id) && (
                                      <span className="checkmark">✓</span>
                                    )}
                                  </button>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {notificationType === 'email' && (
                  <>
                    <div className={styles.formGroup} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <label htmlFor="subject">Email Subject *</label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={emailFormData.subject}
                        onChange={handleInputChange}
                        required
                        maxLength={200}
                        style={{ width: '100%' }}
                      />
                    </div>

                    <div className={styles.formGroup} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <label htmlFor="html">Email Content *</label>
                      <ReactQuill
                        value={emailFormData.html || ''}
                        onChange={(value) => setEmailFormData((prev) => ({ ...prev, html: value }))}
                        theme="snow"
                        modules={{
                          toolbar: [
                            [{ 'header': [1, 2, 3, false] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            ['blockquote', 'code-block'],
                            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                            [{ 'color': [] }, { 'background': [] }],
                            [{ 'align': [] }],
                            ['link', 'image'],
                            ['clean']
                          ]
                        }}
                        placeholder="Write your email content here (formatting is automatic)..."
                        style={{
                          borderRadius: '4px',
                          minHeight: '200px'
                        }}
                      />
                    </div>
                  </>
                )}

                {notificationType === 'in-app' && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div className={styles.formGroup} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <label htmlFor="title">Title *</label>
                        <input
                          type="text"
                          id="title"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                          maxLength={200}
                          style={{ width: '100%' }}
                        />
                      </div>

                      <div className={styles.formGroup} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <label htmlFor="category">Category</label>
                        <select
                          id="category"
                          name="category"
                          value={formData.category || ''}
                          onChange={handleInputChange}
                          style={{ width: '100%' }}
                        >
                          <option value="announcement">Announcement</option>
                          <option value="alert">Alert</option>
                          <option value="update">Update</option>
                          <option value="maintenance">Maintenance</option>
                        </select>
                      </div>
                    </div>

                    <div className={styles.formGroup} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <label htmlFor="content">Content *</label>
                      <textarea
                        id="content"
                        name="content"
                        value={formData.content}
                        onChange={handleInputChange}
                        required
                        rows={5}
                        style={{ width: '100%', minHeight: '120px' }}
                      />
                    </div>
                  </>
                )}

                {notificationType === 'in-app' && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div className={styles.formGroup} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <label htmlFor="priority">Priority *</label>
                        <select
                          id="priority"
                          name="priority"
                          value={formData.priority}
                          onChange={handleInputChange}
                          style={{ width: '100%' }}
                        >
                          <option value="low">Low</option>
                          <option value="normal">Normal</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </div>

                      <div className={styles.formGroup} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <label htmlFor="displayType">Display Type *</label>
                        <select
                          id="displayType"
                          name="displayType"
                          value={formData.displayType}
                          onChange={handleInputChange}
                          style={{ width: '100%' }}
                        >
                          <option value="once">Once (Auto-dismiss after read)</option>
                          <option value="dismissible">Dismissible</option>
                          <option value="persistent">Persistent (Cannot dismiss)</option>
                        </select>
                      </div>
                    </div>

                    <div className={styles.formGroup} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <label htmlFor="expiresAt">Expiration Date (Optional)</label>
                      <input
                        type="datetime-local"
                        id="expiresAt"
                        name="expiresAt"
                        value={formData.expiresAt || ''}
                        onChange={handleInputChange}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </>
                )}
              </form>
            </div>
            <div className={adminCommonStyles.modalFooter}>
              <button
                className={adminCommonStyles.btnCancel}
                onClick={() => setShowCreateForm(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="notificationForm"
                className={adminCommonStyles.btnDeleteConfirm}
                disabled={loading}
              >
                {loading ? (notificationType === 'email' ? 'Sending...' : 'Saving...') : (notificationType === 'email' ? 'Send Email' : (editingNotification ? 'Update' : 'Create'))}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNotifications;
